/* eslint-disable @typescript-eslint/no-explicit-any */
import { withLayout } from '@/layout/Layout';
import { useEffect, useState } from 'react';
import { BreadcrumbsProps, PageProps } from '@/types/types';
import { LayoutProps } from '@/layout/Layout/Layout.props';
import { GetStaticPathsContext, GetStaticProps } from 'next';
import { getApolloClient } from '@/utils/apollo-client';
import { flatListToHierarchical } from '@/utils/flatListToHierarchical';
import { transformMenus } from '@/utils/transformMenus';

import CityIcon from '@/public/icons/team/city.svg';
import { ALL_MENU_QUERY, MenuLocationEnum } from '@/data/allMenuQuery';
import { Section } from '@/components/common/Section';
import { DateFormat } from '@/components/common/DateFormat';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  FooterMenuLocationEnum,
  FOOTER_DATA_QUERY,
  FOOTER_DATA_QUERY_EN,
} from '@/data/footerDataQuery';
import { footerDataTransformer } from '@/utils/footerDataTransformer';
import { PageHead } from '@/components/common/PageHead';
import { TeamForm } from '@/components/forms/TeamForm';
import { ShareButtons } from '@/components/Team/ShareButtons';
import { ALL_TEAM_PATH } from '@/data/allTeamPath';
import { ROUTES } from '@/utils/routes';
import { TEAM_BY_SLUG_QUERY } from '@/data/teamBySlugQuery';
import { TEAM_SLUG_TRANSLATION } from '@/data/teamSlugTranslation';

const Slug = ({ data }: PageProps & LayoutProps): JSX.Element => {
  const [isMounted, setIsMounted] = useState(false);
  const { t } = useTranslation('common');
  const shareVacancyText = t('shareVacancy');
  const requirementsText = t('requirements');
  const responsibilitiesText = t('responsibilities');
  const weOfferText = t('weOffer');
  const readyText = t('ready');
  const valueToStore = data?.teamBy?.translatedSlug || '';

  const { title, date, vacancies, teamCities, teamFormats, teamTypes, uri } =
    data?.teamBy ?? {};
  const { seo: seoParent } = data?.pageBy?.translation ?? {};
  const breadcrumbs = [
    ...(seoParent?.breadcrumbs ?? []),
    {
      text: title,
      url: uri,
    },
  ] as BreadcrumbsProps[];

  useEffect(() => {
    localStorage.setItem('translated_slug', valueToStore);
    return () => {
      localStorage.removeItem('translated_slug');
    };
  }, [valueToStore]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <PageHead
        title={seoParent?.breadcrumbTitle || title}
        seo={seoParent || {}}
        canonicalUrl={seoParent?.canonicalUrl}
        breadcrumbs={breadcrumbs}
        descriptionChild={seoParent?.description}
      />
      {isMounted && (
        <>
          <div className="team-vacancy__main-wrapper container">
            <Section
              className="team-vacancy__section pb-[60px] md:pb-[100px]"
              breadcrumbs={breadcrumbs}
              lazy={false}
              breadcrumbsMaxLength={15}
            >
              <div className="team-vacancy">
                {date && (
                  <p className="dark:gray-scale-red-diff team-vacancy__date">
                    <DateFormat date={date} />
                  </p>
                )}
                <h1 className="team-vacancy__title">{title}</h1>
                <div className="vacancy-card__tags flex flex-wrap">
                  <div className="vacancy-card__tag w-fit px-3 py-1">
                    {teamFormats?.edges[0]?.node?.name}
                  </div>
                  <div className="vacancy-card__tag w-fit px-3 py-1">
                    {teamTypes?.edges[0]?.node?.name}
                  </div>

                  {teamCities?.edges?.map(({ node }, idx) => (
                    <div
                      key={idx}
                      className="vacancy-card__tag vacancy-card__tag--city w-fit px-3 py-1 md:px-[14px] md:py-[6px]"
                    >
                      <CityIcon /> {node?.name}
                    </div>
                  ))}
                </div>
              </div>
              <div className="team-vacancy__description">
                {vacancies.teamText && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: vacancies.teamText,
                    }}
                    className="last:mb-0"
                  />
                )}{' '}
                {vacancies.teamCandidateRequirements && (
                  <div>
                    <h3> {requirementsText}</h3>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: vacancies.teamCandidateRequirements,
                      }}
                      className="last:mb-0"
                    />
                  </div>
                )}{' '}
                {vacancies.teamMainResponsibilities && (
                  <div>
                    <h3> {responsibilitiesText}</h3>{' '}
                    <div
                      dangerouslySetInnerHTML={{
                        __html: vacancies.teamMainResponsibilities,
                      }}
                      className="last:mb-0"
                    />{' '}
                  </div>
                )}{' '}
                {vacancies.teamConditionsBenefits && (
                  <div>
                    <h3> {weOfferText}</h3>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: vacancies.teamConditionsBenefits,
                      }}
                      className="last:mb-0"
                    />
                  </div>
                )}{' '}
              </div>
              <div
                className="team-vacancy__ready"
                dangerouslySetInnerHTML={{
                  __html: readyText,
                }}
              />

              <div className="team-vacancy__share">
                <p className="team-vacancy__share-title">{shareVacancyText}</p>
                <ShareButtons />
              </div>
            </Section>
            <Section className="team-form-section">
              <TeamForm />
            </Section>
          </div>
        </>
      )}
    </>
  );
};

export default withLayout(Slug);

//LABEL: PROPS
export const getStaticProps: GetStaticProps = async context => {
  const client = getApolloClient();

  const slug = `/${context.params?.slug}` || ROUTES.HOME;
  const language = context.locale?.toUpperCase();

  const location =
    context.locale == 'uk'
      ? MenuLocationEnum.PRIMARY_NAVIGATION
      : MenuLocationEnum.PRIMARY_NAVIGATION___EN;

  const slugLanguage = context.locale == 'uk' ? 'EN' : 'UK';

  const { data } = await client.query({
    query: TEAM_BY_SLUG_QUERY,
    variables: {
      slug,
      language,
    },
  });
  let translatedSlug = '';

  try {
    const { data: translatedData } = await client.query({
      query: TEAM_SLUG_TRANSLATION,
      variables: {
        slug,
        language: slugLanguage,
      },
    });

    translatedSlug =
      (translatedData?.teamBy?.translation?.slug as string) ?? '';
  } catch (e) {
    console.log('ERROR');
  }

  const newData = {
    ...data,
    teamBy: {
      ...data.teamBy,
      translatedSlug,
    },
  };

  const {
    data: { menuItems },
  } = await client.query({
    query: ALL_MENU_QUERY,
    variables: {
      location,
    },
  });

  const footerLocation =
    context.locale == 'uk'
      ? FooterMenuLocationEnum.FOOTER_MENU
      : FooterMenuLocationEnum.FOOTER_MENU___EN;

  const { data: footerMenuItems } =
    context.locale == 'uk'
      ? await client.query({
          query: FOOTER_DATA_QUERY,
          variables: {
            location: footerLocation,
          },
        })
      : await client.query({
          query: FOOTER_DATA_QUERY_EN,
          variables: {
            location: footerLocation,
          },
        });
  const footerData = footerDataTransformer(footerMenuItems);

  const flatList = flatListToHierarchical(menuItems.nodes);
  const menus = transformMenus(flatList);

  return {
    props: {
      data: newData,
      menus,
      footerData,
      ...(await serverSideTranslations(context.locale as string, ['common'])),
    },
  };
};

// LABEL: PATHS
export const getStaticPaths = async ({ locales }: GetStaticPathsContext) => {
  const client = getApolloClient();

  const { data } = await client.query({
    query: ALL_TEAM_PATH,
  });

  const paths = data.allTeam.nodes.flatMap(({ slug }: { slug: string }) => {
    return locales?.map(locale => ({
      params: {
        slug,
      },
      locale,
    }));
  });

  return {
    paths,
    fallback: false,
  };
};
